import SockJS, { Options as ISockOptions } from 'sockjs-client';

import { Auth } from '../../auth/Auth';
import { IDoryConfig } from '../../interface/IDoryConfig';
import { Logger, LogSender } from '../../logger';
import { DefaultStreaming } from './default/DefaultStreaming';
import {
  DefaultConnectionsExceededResponse,
  getDefaultCancelRequest,
} from './default/Messages';
import StreamingError, { StreamingErrorCodes } from './StreamingError';
import { StreamingEventBus } from './StreamingEventBus';
import {
  DoryServiceType,
  DoryStreamingMessage,
  DoryMessageType,
} from './StreamingMessages';
import StreamingMonitor, { IStreamingMonitorOptions } from './StreamingMonitor';
import StreamingService from './StreamingService';

const RECONNECT_TIMEOUT = 4000;

//#region Interfaces
export type IStreamingOptions = {
  monitor?: IStreamingMonitorOptions;
  visibilityMonitorEnabled?: boolean;
} & ISockOptions;
//#endregion

export type ConnectionLostListener = (e: boolean) => void;
export type ConnectionRestoredListener = (e: boolean) => void;
export type ConnectionPausedListener = (e: boolean) => void;
export type ConnectionExceededListener = (
  status: number,
  message: string
) => void;
/**
 * This service offers streaming of RICs. The service creates a low latency, full duplex, cross-domain communication channel between the browser and the web server. Under the hood `SockJS` library tries to use native  * WebSockets first. If that fails it can use a variety of browser-specific transport protocols and presents them through WebSocket-like abstractions.
 *
 * Because of the full duplex communication, most Streaming methods do not support promises and instead rely on callbacks.
 *
 * ---
 *
 * ### How to use
 *
 * - `Streaming` only supports one active WebSocket connection, because of this:
 *   - `connect()` not create a new connection when:
 *     - another connect attempt is pending ⇒ return pending `promise`
 *     - already connected to WebSocket ⇒ return `true`
 *     - already reconnecting ⇒ throw Error (`Socket not available`)
 *   - `connect()` will register callbacks only on the first successful call
 * - `Streaming` registers a heartbeat function in order to monitor connection
 *   - can be configured when calling `connect(options, ...)` via `options.monitor`
 *   - when a heartbeat tick does not complete `Streaming` will:
 *     - close connection to WebSocket
 *     - call all callbacks passed to `addConnectionLostListener()`
 *     - start reconnecting (every 4 seconds)
 *     - ⚠️ while reconnecting new subscriptions are not registered & thus will not be resubscribed
 *   - when reconnect is successful `Streaming` will:
 *     - test the connection
 *     - restart the heartbeat
 *     - resubscribing all previous subscriptions
 *     - call all callbacks passed to `addConnectionRestoredListener()`
 *   - ⚠️ reconnecting does not connect to the old connection (which most likely was closed by Server), but starts a new one
 *   - ⚠️ resubscribing subscriptions does not connect to the old subscriptions, but starts new ones
 * - Use `addConnectionLostListener()` to:
 *   - update global application state
 *   - prevent new subscriptions
 * - Use `addConnectionRestoredListener()` to:
 *   - update global application state
 * - `Quote.subscribe()` and `News.subscribe()`
 *   - Individual subscriptions are not notified if the connection to the connected websocket is lost
 *   - Resubscribing a subscription creates a brand new subscription & will call `subscribe` and `snapshot` callbacks
 *   - The application using `Streaming` needs to handle `disconnected`/`reconnecting` state for subscriptions
 */
export default abstract class Streaming {
  //#region SDK
  protected config: IDoryConfig;
  private serviceAuth: Auth;
  /**
   * @hidden
   */
  public get Auth(): Auth {
    return this.serviceAuth;
  }
  //#endregion

  //#region Endpoints
  /**
   * Set sockjs-server endpoint
   *
   * @param newEndpoint full url to new endpoint
   */
  public abstract setSockEndpoint(newEndpoint: string): void;
  /**
   * Get sockjs-server endpoint
   */
  public abstract getSockEndpoint(): string;

  /**
   * Set websocket-server endpoint
   *
   * @param newEndpoint full url to new endpoint
   */
  public abstract setWebSocketEndpoint(newEndpoint: string): void;
  /**
   * Get websocket-server endpoint
   */
  public abstract getWebSocketEndpoint(): string;
  //#endregion

  //#region Fields
  private eventBus: StreamingEventBus;
  /**
   * @hidden
   */
  public get EventBus(): StreamingEventBus {
    return this.eventBus;
  }

  private timeout = 3000;
  /**
   * Global timeout for streaming methods
   */
  public get Timeout(): number {
    return this.timeout;
  }
  public set Timeout(timeout: number) {
    this.timeout = timeout;
  }

  protected connected = false;
  /**
   * Check this property to see if client is connected to socket
   */
  public get isConnected(): boolean {
    return this.connected;
  }

  private connectionLostListeners: ConnectionLostListener[] = [];
  private connectionRestoredListeners: ConnectionRestoredListener[] = [];
  private connectionPausedListeners: ConnectionPausedListener[] = [];
  private connectionExceededListeners: ConnectionExceededListener[] = [];
  private latency = 0;
  /**
   * Check this property to see heartbeat latency in ms
   */
  public get Latency(): number {
    return this.latency;
  }

  /**
   * Check this property to see if WebSocket Server is available
   * @deprecated Use "isConnected" instead
   */
  public get isServerAvailable(): boolean {
    return this.isConnected;
  }

  private visibilityMonitorEnabled = false;
  /**
   * Check this property to see if the document visibility monitor option is enabled
   */
  public get isVisibilityMonitorEnabled(): boolean {
    return this.visibilityMonitorEnabled;
  }
  /**
   * @hidden
   * @deprecated Use "isVisibilityMonitorEnabled" instead
   */
  public get isVisiblityMonitorEnabled(): boolean {
    return this.visibilityMonitorEnabled;
  }
  //#endregion

  /**
   * Underlying WebSocket Object
   */
  private socket: WebSocket;
  /**
   * We register a timer to check each second if TTL of current jwt token is less than 60s
   * This callback can cancel timer in case of disconnect, connection loss
   */
  private cancelTokenRefresh: ReturnType<typeof setInterval>;
  /**
   * Id of reconnect timer
   */
  private cancelReconnect: ReturnType<typeof setTimeout>;
  /**
   * Keep track of ongoing connect to prevent parallel connections
   */
  private pendingConnect: Promise<boolean>;
  /**
   * Backup of connection options
   */
  private connectOptions: {
    options: ISockOptions | Record<string, unknown>;
    error: (error: unknown) => void;
  };

  //#region Streaming Services
  private streamingMonitor: StreamingMonitor;
  private defaultStreaming: DefaultStreaming;
  public abstract get isServiceAvailable(): Record<string, boolean>;
  //#endregion

  /**
   * @hidden
   */
  constructor(config: IDoryConfig, serviceAuth: Auth) {
    this.serviceAuth = serviceAuth;
    this.serviceAuth.addSessionExpiredListener(() => {
      if (this.isConnected) {
        this.disconnect();
      }
    });

    this.config = config;
    this.eventBus = new StreamingEventBus();
    this.defaultStreaming = new DefaultStreaming(this, this.serviceAuth);

    // Init NetworkMonitor
    this.streamingMonitor = new StreamingMonitor(this);
    this.streamingMonitor.addDisconnectListener((err: StreamingError) =>
      this.connectionLost(err)
    );

    // Page visibility event listeners
    this.streamingMonitor.addVisibilityListener(
      this.handleVisibilityChange.bind(this)
    );

    this.EventBus.on(
      DoryServiceType.DEFAULT,
      DoryMessageType.CONNECTIONS_EXCEEDED,
      (data: DefaultConnectionsExceededResponse) => {
        this.connectionsExceeded(data.payload.status, data.payload.message);
      }
    ).bind(this);
  }

  /**
   * Connect to Websocket
   *
   * A heartbeat is registered when `connect()` is successful:
   *
   * - After connecting the SDK registers a heartbeat to monitor the connection (`options.monitor.interval`)
   * - If the heartbeat does not complete in a set time (`options.monitor.timeout`)
   *   - A callback is invoked so the UI can be updated to display the outage
   *   - The SDK starts reconnect attempts (every 4 seconds)
   * - If the SDK reconnects to the Websocket, it will:
   *   - test the connection
   *   - restart the heartbeat
   *   - restores all previous subscriptions (News & Quote)
   *   - A callback is invoked so the UI can be updated to display the end of the outage
   *
   * ```ts
   * // Options Interface:
   * {
   *   server: string,
   *   transports: string | string[],
   *   sessionId: number | (() => string),
   *   monitor: {
   *     interval?: number,
   *     timeout?: number
   *   }
   *   visibilityMonitorEnabled: boolean
   * }
   * ```
   *
   * ```js example
   * dory.<Module>.connect()
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function () {
   *     // Is Connected?
   *     console.log('Dory Streaming connected: ' + dory.<Module>.isConnected);
   *
   *     // Ping Server
   *     dory.<Module>.ping(function (data, err) {
   *       if (err) {
   *         console.error(err);
   *       } else {
   *         console.log('Ping', data);
   *       }
   *     });
   *
   *     // Unsubscribe after 10 seconds
   *     window.setTimeout(function () {
   *       dory.<Module>.disconnect().then(function () {
   *         // Is Connected?
   *         console.log('Dory Streaming connected: ' + dory.<Module>.isConnected);
   *       });
   *     }, 10000);
   *   });
   * ```
   *
   * @param options  SockJS options object
   * @param error  Callback when WebSocket connection throws error
   */
  public async connect(
    options: IStreamingOptions = {},
    error?: (error: unknown) => void
  ): Promise<boolean> {
    this.visibilityMonitorEnabled = options.visibilityMonitorEnabled;

    // Return current login attempt
    if (this.pendingConnect) {
      return this.pendingConnect;
    }
    // Return true if already connected
    if (this.connected) {
      return true;
    }
    this.pendingConnect = this.Auth.isSessionValid()
      .then((isSessionValid: boolean) => {
        if (!isSessionValid) {
          throw new StreamingError(
            'Streaming.connect',
            StreamingErrorCodes.SESSION_INVALID,
            'Invalid Session'
          );
        }

        return this.Auth.refreshToken();
      })
      .catch(() => {
        this.pendingConnect = null;
        throw new StreamingError(
          'Streaming.connect',
          StreamingErrorCodes.REFRESH_TOKEN_FAILED,
          'Could not refresh Token'
        );
      })
      .then(() => this.isAllowedStreaming())
      .then((isAllowedStreaming) => {
        if (!isAllowedStreaming) {
          this.pendingConnect = null;
          Logger.error(
            LogSender.STREAMING,
            'Streaming permission not present for user'
          );
          throw new StreamingError(
            'Streaming.connect',
            StreamingErrorCodes.NO_STREAMING_PERMISSION,
            'Streaming permission not present for user'
          );
        }
      })
      .then(() => this.Auth.getTokenString())
      .then((token: string) => {
        return new Promise<boolean>((resolve, reject) => {
          const cancelTimeout = setTimeout(() => {
            this.pendingConnect = null;
            this.socket.removeEventListener('close', connectFailedHandler); // Remove connect failed handler
            // if we force a timeout, cancel the existing connect
            this.socket.close();
            reject(
              new StreamingError(
                'Streaming.connect',
                StreamingErrorCodes.TIMEOUT,
                `WebSocket connect timed out! No response in ${this.Timeout}ms`
              )
            );
          }, this.Timeout);
          const connectFailedHandler = (event: CloseEvent) => {
            if (event?.type === 'close') {
              this.pendingConnect = null;
              this.socket.removeEventListener('close', connectFailedHandler); // Remove connect failed handler
              clearTimeout(cancelTimeout); // cancel timeout timer
              this.socket.close();
              reject(
                new StreamingError(
                  'Streaming.connect',
                  StreamingErrorCodes.CONNECTION_DENIED,
                  `Unable to connect to server.`
                )
              );
            }
          };
          Logger.silly(
            LogSender.STREAMING,
            `Started connect timeout timer (id: ${cancelTimeout})`
          );

          this.socket = new SockJS(
            `${this.getSockEndpoint()}?jwt=${token}`,
            null, // sockJS reserved
            options
          );

          this.socket.addEventListener('close', connectFailedHandler);
          this.socket.onopen = () => {
            Logger.info(LogSender.STREAMING, 'Connected to WebSocket');
            // Backup connect parameters
            this.connectOptions = {
              options,
              error,
            };

            // Cache current token so that we can send token-refresh if token changed from outside
            let cachedToken = token;
            // Register token renewal
            this.cancelTokenRefresh = setInterval(async () => {
              const token = await this.Auth.getTokenString();
              const { msLeft, exp } = await this.Auth.getToken();
              if (msLeft <= 60000) {
                this.refreshToken(async () => {
                  cachedToken = await this.Auth.getTokenString();
                });
                Logger.silly(
                  LogSender.STREAMING,
                  `Requested new token, exp of current token is ${exp}ms(expiring in ${msLeft}ms)`
                );
              }
              // Token changed from outside. Send `token-refresh`.
              if (token !== cachedToken) {
                cachedToken = token;
                this.defaultStreaming.sendTokenRefresh();
                Logger.silly(
                  LogSender.STREAMING,
                  'Updated token after token was updated outside of Streaming.'
                );
              }
            }, 10000);
            Logger.silly(LogSender.STREAMING, 'Started Token refresh interval');

            // Start StreamingMonitor
            this.streamingMonitor.start(
              (options as IStreamingOptions).monitor || {},
              this.Services
            );

            clearTimeout(cancelTimeout); // cancel timeout timer
            this.socket.removeEventListener('close', connectFailedHandler); // Remove connect failed handler
            Logger.silly(
              LogSender.STREAMING,
              `Stopped connect timeout timer (id: ${cancelTimeout})`
            );

            this.connected = true; // set connected status
            this.pendingConnect = null; // reset pending connection
            resolve(true);
          };

          this.socket.onclose = this.socketCloseHandler.bind(this);
          this.socket.onmessage = this.socketMessageHandler.bind(this);
          if (error) {
            this.socket.onerror = error.bind(this);
          }
        });
      });

    return this.pendingConnect;
  }

  /**
   * Disconnect from Websocket and cancel all subscriptions
   *
   * ```js example
   * dory.<Module>.disconnect()
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     console.log(result);
   *   });
   * ```
   */
  public disconnect(): Promise<boolean> {
    if (this.isConnected) {
      const msg = getDefaultCancelRequest();

      return new Promise<boolean>((resolve, reject) => {
        const cancelTimeout = setTimeout(() => {
          reject(
            new StreamingError(
              'Streaming.disconnect',
              StreamingErrorCodes.TIMEOUT,
              'Websocket disconnect timed out!'
            )
          );
        }, this.Timeout);
        this.socket.onclose = (evt) => {
          clearTimeout(cancelTimeout);

          // Clear all stored subscription ids
          this.clearSubscriptions();

          this.socketCloseHandler(evt);
          resolve(true);
        };
        this.socket.send(JSON.stringify(msg));
        // https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent#Status_codes
        this.socket.close(1000);
      });
    } else {
      throw new StreamingError(
        'Streaming.disconnect',
        StreamingErrorCodes.NOT_CONNECTED,
        `Can't disconnect, currently not connected to Socket.`
      );
    }
  }

  protected abstract clearSubscriptions(): void;
  protected abstract resubscribeSubscriptions(): void;

  /**
   * Reconnect after connection loss
   * Recursively calls itself, until reconnect is successful
   * @param skipDelay  Skip delay before reconnect
   */
  private reconnect(skipDelay = false) {
    if (!this.cancelReconnect) {
      const delay = skipDelay ? 0 : RECONNECT_TIMEOUT;
      this.cancelReconnect = setTimeout(() => {
        if (!this.isConnected) {
          Logger.silly(LogSender.STREAMING, 'Starting reconnect attempt');
          this.connect(this.connectOptions.options, this.connectOptions.error)
            .then((success: boolean) => {
              if (success) {
                return this.pingAsync(this.streamingMonitor.Timeout);
              } else {
                throw new Error('Connect failed!');
              }
            })
            .then(() => {
              // Reconnect successful
              Logger.error(
                LogSender.STREAMING,
                'Connection to WebSocket restored!'
              );
              clearTimeout(this.cancelReconnect);
              this.cancelReconnect = null;
              // Trigger the registered connection restored listeners
              for (const listener of this.connectionRestoredListeners) {
                // Using try catch to prevent an error in the listener to cause this reconnect to fail
                try {
                  listener(true);
                } catch (e) {
                  Logger.error(
                    LogSender.STREAMING,
                    'call to connectionRestoredListener failed',
                    e
                  );
                }
              }

              // Call streaming-services resubscribe
              this.resubscribeSubscriptions();
            })
            .catch((err) => {
              Logger.error(
                LogSender.STREAMING,
                `Reconnect attempt failed: ${err}`
              );
              this.cancelReconnect = null;
              // Reconnect unsuccessful, swallow error
              this.reconnect();
            });
        }
      }, delay);
    }
  }

  /**
   * Send message to websocket.
   * @hidden
   * @param message  message object
   */
  public send<T extends DoryStreamingMessage = DoryStreamingMessage>(
    message: string | T
  ): void {
    if (typeof message === 'string') {
      this.socket.send(message);
    } else {
      this.socket.send(JSON.stringify(message));
    }
  }

  /**
   * Register a callback function to get informed when the streaming connection is paused.
   * @category Event-Listener
   *
   * @param listener The function that receives the notification
   */
  public addConnectionPausedListener(listener: ConnectionPausedListener): void {
    this.connectionPausedListeners.push(listener);
  }

  /**
   * Deregister connection paused Listeners
   * @category Event-Listener
   *
   * @param listener The EventListener function of the event handler to remove from the event target.
   */
  public removeConnectionPausedListener(
    listener: ConnectionPausedListener
  ): void {
    const filterListeners = (l: ConnectionPausedListener) => l !== listener;

    this.connectionPausedListeners =
      this.connectionPausedListeners.filter(filterListeners);
  }

  /**
   * Register a callback function to get informed when the streaming connection is lost.
   * @category Event-Listener
   *
   * @param listener The function that receives the notification
   */
  public addConnectionLostListener(listener: ConnectionLostListener): void {
    this.connectionLostListeners.push(listener);
  }

  /**
   * Deregister connection lost Listeners
   * @category Event-Listener
   *
   * @param listener The EventListener function of the event handler to remove from the event target.
   */
  public removeConnectionLostListener(listener: ConnectionLostListener): void {
    const filterListeners = (l: ConnectionLostListener) => l !== listener;

    this.connectionLostListeners =
      this.connectionLostListeners.filter(filterListeners);
  }

  /**
   * Register a callback function to get informed when the streaming connection is restored.
   * @category Event-Listener
   *
   * @param listener The function that receives the notification
   */
  public addConnectionRestoredListener(
    listener: ConnectionRestoredListener
  ): void {
    this.connectionRestoredListeners.push(listener);
  }

  /**
   * Deregister connection restored Listeners
   * @category Event-Listener
   *
   * @param listener The EventListener function of the event handler to remove from the event target.
   */
  public removeConnectionRestoredListener(
    listener: ConnectionRestoredListener
  ): void {
    const filterListeners = (l: ConnectionRestoredListener) => l !== listener;

    this.connectionRestoredListeners =
      this.connectionRestoredListeners.filter(filterListeners);
  }

  /**
   * Register a callback function to get informed when the streaming connections are exceeded
   * @category Event-Listener
   *
   * @param listener The function that receives the notification
   */
  public addConnectionsExceededListener(
    listener: ConnectionExceededListener
  ): void {
    this.connectionExceededListeners.push(listener);
  }

  /**
   * Deregister connections exceeded Listeners
   * @category Event-Listener
   *
   * @param listener The EventListener function of the event handler to remove from the event target.
   */
  public removeConnectionsExceededListener(
    listener: ConnectionExceededListener
  ): void {
    const filterListeners = (l: ConnectionExceededListener) => l !== listener;

    this.connectionExceededListeners =
      this.connectionExceededListeners.filter(filterListeners);
  }

  //#region Socket Handler
  private socketCloseHandler(evt: CloseEvent): void {
    /*
    /  manual close
    /  type: "close"
    /  code: 1000
    /  reason: "Normal closure"
    /  ----- -----
    /  socket close (by server and safari bug)
    /  type: "close"
    /  code: 3000
    /  reason: "Go away!"
    */
    if (this.cancelTokenRefresh) {
      clearInterval(this.cancelTokenRefresh);
    }
    this.streamingMonitor.stop();

    // Clean up connection state
    this.connected = false;
    this.pendingConnect = undefined;

    if (evt && (evt.code === 3000 || evt.code === 1006)) {
      Logger.error(
        LogSender.STREAMING,
        `Connection to WebSocket closed! (${evt.code})\n`
      );
      this.socket.close();
      // Start reconnect attempts
      this.reconnect();
    }
  }
  private socketMessageHandler(e: MessageEvent): void {
    const response = JSON.parse(e.data) as DoryStreamingMessage;
    const header = response.header;

    this.EventBus.emit(header.service, header.messageType, response);
  }

  /**
   * This function will be executed when heartbeat detects that the WebSocket Server is offline
   * @param socket  Current WebSocket
   * @param reason  Reason for connection loss
   */
  private connectionLost(error: StreamingError) {
    if (error.status !== StreamingErrorCodes.SERVICE_UNAVAILABLE) {
      Logger.error(
        LogSender.STREAMING,
        `Connection to WebSocket lost!\n${error.status}`
      );
      this.socket.close();

      // Trigger the registered connection lost listeners
      for (const listener of this.connectionLostListeners) {
        try {
          listener(true);
        } catch (e) {
          Logger.error(
            LogSender.STREAMING,
            `call to connectionLostListener failed`,
            e
          );
        }
      }

      // Start reconnect attempts
      this.reconnect();
    } else {
      Logger.error(
        LogSender.STREAMING,
        `Connection to Datasource lost!\n${error.status}\n${JSON.stringify(
          this.isServiceAvailable,
          null,
          2
        )}`
      );
    }
  }

  private connectionsExceeded(status: number, message: string) {
    Logger.error(
      LogSender.STREAMING,
      `Connection to Datasource exceeded!\n${message}\nDisconnecting...`
    );

    this.streamingMonitor.stop();
    this.disconnect();

    // Trigger the registered connection exceeded listeners
    for (const listener of this.connectionExceededListeners) {
      try {
        listener(status, message);
      } catch (e) {
        Logger.error(
          LogSender.STREAMING,
          `call to connectionExceededListener failed`,
          e
        );
      }
    }
  }

  //#endregion

  //#region Streaming Services
  public abstract get Services(): StreamingService<unknown>[];

  /**
   * Ping Socket
   * @param callback  Executed when ping response is received
   */
  public ping(
    callback?: (data: DoryStreamingMessage, err?: StreamingError) => void
  ): void {
    this.defaultStreaming.ping(callback);
  }
  /**
   * Ping Socket
   * @param timeout  timeout in ms
   * @returns response  The Dory Ping Response
   */
  public pingAsync(timeout: number): Promise<DoryStreamingMessage> {
    return new Promise((resolve, reject) => {
      const cancelPingTimeout = setTimeout(reject, timeout);
      this.ping((data, err) => {
        clearTimeout(cancelPingTimeout);
        if (err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
  }
  /**
   * Refresh jwt token and send it to socket
   * @param callback  Executed when refreshToken response is received
   */
  public refreshToken(
    callback?: (data: DoryStreamingMessage, err?: StreamingError) => void
  ): void {
    const startTime = Date.now();
    this.defaultStreaming.refreshToken(
      (data: DoryStreamingMessage, err?: StreamingError) => {
        this.latency = Date.now() - startTime;

        Logger.silly(
          LogSender.STREAMING,
          `Refreshed token in ${this.latency}ms`
        );
        if (callback) callback(data, err);
      }
    );
  }

  /**
   * Identifies if user has streaming permission
   * @returns boolean
   */
  public abstract isAllowedStreaming(): Promise<boolean>;
  //#endregion

  //#region Page visibility handler
  /**
   * Close/Re-open WebSocket based on the page visibility
   */
  private handleVisibilityChange(hidden: boolean) {
    if (hidden) {
      if (this.isConnected) {
        Logger.warn(
          LogSender.STREAMING,
          'Tab VisibilityChange: hidden, disconnecting socket'
        );
        const msg = getDefaultCancelRequest();
        this.socket.send(JSON.stringify(msg));
        // https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent#Status_codes
        this.socket.close(1000);
        // Trigger the registered connection lost listeners
        for (const listener of this.connectionPausedListeners) {
          try {
            listener(true);
          } catch (e) {
            Logger.error(
              LogSender.STREAMING,
              `call to connectionPausedListener failed`,
              e
            );
          }
        }
      }
    } else {
      if (!this.isConnected) {
        Logger.warn(
          LogSender.STREAMING,
          'Tab VisibilityChange: visible, reconnecting socket'
        );
        this.reconnect(true);
      }
    }
  }
  //#endregion
}
